import React, { useEffect, useState } from 'react';
import { Plus, ArrowRight, Users } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';

const GROUP_STATUS_LABELS = { draft: 'Draft', scheduled: 'Scheduled', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled' };
const GROUP_STATUS_BADGE = { draft: 'badge-warn', scheduled: 'badge-neutral', in_progress: 'badge-warn', completed: 'badge-ok', cancelled: 'badge-neutral' };
const PROGRAM_LABELS = { php: 'PHP', iop: 'IOP', psr: 'PSR' };

function emptyForm() {
  return {
    id: null, name: '', group_type: '', topic: '', therapeutic_purpose: '', intervention: '',
    clinical_population: '', therapeutic_method: '', expected_outcome: '', material_discussed: '', skills_practiced: '', overall_group_process: '',
    facilitator_id: '', co_facilitator_id: '', session_start: '', session_end: '', program_type: 'php', status: 'draft', location: '', max_participants: '',
  };
}

export default function PhpGroupSessions({ user, notify, navigate }) {
  const canManage = hasCap(user, 'therapy.manage');
  const [sessions, setSessions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setSessions([]); return; }
      const [s, u] = await Promise.all([
        api('/php/group-sessions'),
        api('/clinical-message-recipients').catch(() => ({ users: [] })),
      ]);
      setSessions(s.sessions || []);
      setStaff(u.users || []);
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function startNew() { setForm(emptyForm()); setEditing(true); }
  function startEdit(s) {
    setForm({
      id: s.id, name: s.name || '', group_type: s.group_type || '', topic: s.topic || '', therapeutic_purpose: s.therapeutic_purpose || '', intervention: s.intervention || '',
      clinical_population: s.clinical_population || '', therapeutic_method: s.therapeutic_method || '', expected_outcome: s.expected_outcome || '', material_discussed: s.material_discussed || '', skills_practiced: s.skills_practiced || '', overall_group_process: s.overall_group_process || '',
      facilitator_id: s.facilitator_id || '', co_facilitator_id: s.co_facilitator_id || '', session_start: s.session_start || '', session_end: s.session_end || '', program_type: s.program_type || 'php', status: s.status || 'draft', location: s.location || '', max_participants: s.max_participants ?? '',
    });
    setEditing(true);
  }

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Group session saved (demo).'); setEditing(false); return; }
      if (form.id) await api(`/php/group-sessions/${form.id}`, { method: 'PATCH', body: form });
      else await api('/php/group-sessions', { method: 'POST', body: form });
      notify('Group session saved.');
      setEditing(false);
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <section className="registry-panel practice-page">
      <div className="content-heading">
        <div><h3>Group psychotherapy sessions <span className="badge neutral">{sessions.length}</span></h3><p>Facility group resources. A group holds shared documentation once; each participant gets an individualized response — never cloned.</p></div>
        {canManage && <button type="button" className="button primary" onClick={startNew}><Plus size={14} /> New group</button>}
      </div>

      {sessions.length === 0 && <div className="empty-state"><Users size={28}/><h3>No group sessions</h3><p>Create a group to begin documenting group psychotherapy.</p></div>}
      {sessions.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead><tr><th>Group</th><th>Program</th><th>Status</th><th>Scheduled</th><th>Facilitator</th><th>Responses</th><th>Signed</th><th></th></tr></thead>
            <tbody>{sessions.map(s => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td><span className="badge neutral">{PROGRAM_LABELS[s.program_type] || s.program_type}</span></td>
                <td><span className={`badge ${GROUP_STATUS_BADGE[s.status] || 'badge-neutral'}`}>{GROUP_STATUS_LABELS[s.status] || s.status}</span></td>
                <td>{s.session_start ? new Date(s.session_start).toLocaleString() : '—'}</td>
                <td>{s.facilitator_name || '—'}</td>
                <td>{s.response_count ?? 0}</td>
                <td>{s.signed_count ?? 0}</td>
                <td><button className="text-button" onClick={() => navigate(`/php-therapy/group/${s.id}`)}>Open <ArrowRight size={13}/></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal title={form.id ? 'Edit group session' : 'New group session'} onClose={() => setEditing(false)} wide>
          <form className="php-form" onSubmit={submit}>
            <div className="form-grid">
              <label>Group name<input value={form.name} onChange={set('name')} maxLength={200} required /></label>
              <label>Group type<input value={form.group_type} onChange={set('group_type')} placeholder="e.g. process, skills" /></label>
              <label>Program<select value={form.program_type} onChange={set('program_type')}>{Object.entries(PROGRAM_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
              <label>Status<select value={form.status} onChange={set('status')}>{Object.entries(GROUP_STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
              <label>Scheduled start<input type="datetime-local" value={form.session_start} onChange={set('session_start')} /></label>
              <label>Scheduled end<input type="datetime-local" value={form.session_end} onChange={set('session_end')} /></label>
              <label>Facilitator
                <select value={form.facilitator_id} onChange={set('facilitator_id')}>
                  <option value="">— Unset —</option>
                  {staff.map(u => <option key={u.id} value={u.id}>{u.name}{u.title ? ` · ${u.title}` : ''}</option>)}
                </select>
              </label>
              <label>Co-facilitator
                <select value={form.co_facilitator_id} onChange={set('co_facilitator_id')}>
                  <option value="">— Unset —</option>
                  {staff.map(u => <option key={u.id} value={u.id}>{u.name}{u.title ? ` · ${u.title}` : ''}</option>)}
                </select>
              </label>
              <label>Max participants<input type="number" min="1" max="200" value={form.max_participants} onChange={set('max_participants')} placeholder="Default 12" /></label>
              <label>Location<input value={form.location} onChange={set('location')} maxLength={200} /></label>
              <label className="span2">Topic<input value={form.topic} onChange={set('topic')} /></label>
              <label className="span2">Clinical population<textarea rows={2} value={form.clinical_population} onChange={set('clinical_population')} placeholder="Who this group serves" /></label>
              <label className="span2">Therapeutic method<textarea rows={2} value={form.therapeutic_method} onChange={set('therapeutic_method')} placeholder="e.g. CBT skills, DBT, psychoeducation" /></label>
              <label className="span2">Expected outcome<textarea rows={2} value={form.expected_outcome} onChange={set('expected_outcome')} placeholder="What participants should gain" /></label>
              <label className="span2">Therapeutic purpose<textarea rows={2} value={form.therapeutic_purpose} onChange={set('therapeutic_purpose')} /></label>
              <label className="span2">Intervention<textarea rows={2} value={form.intervention} onChange={set('intervention')} /></label>
              <label className="span2">Material discussed<textarea rows={2} value={form.material_discussed} onChange={set('material_discussed')} /></label>
              <label className="span2">Skills practiced<textarea rows={2} value={form.skills_practiced} onChange={set('skills_practiced')} /></label>
              <label className="span2">Overall group process<textarea rows={2} value={form.overall_group_process} onChange={set('overall_group_process')} /></label>
            </div>
            <div className="php-plan-actions">
              <button className="button primary" disabled={busy}>{busy ? 'Saving…' : form.id ? 'Update group' : 'Create group'}</button>
              <button type="button" className="button ghost" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
